import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  Shield, 
  Video, 
  Clock, 
  FileCheck, 
  Calculator, 
  Building2, 
  TrendingUp, 
  BookOpen, 
  Briefcase,
  FileWarning,
  Bot,
  MapPin,
  Globe,
  Users,
  ClipboardCheck,
  CalendarCheck,
  Upload,
  Send,
  Star,
  ArrowRight,
  ExternalLink,
  Check,
  CreditCard
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useSettings } from "@/context/SettingsContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const trustItems = [
  { icon: MapPin, label: "NYC-Based" },
  { icon: Globe, label: "Nationwide Service" },
  { icon: Shield, label: "Secure Client Portal" },
  { icon: Video, label: "Virtual Convenience" },
  { icon: Users, label: "Individual & Business" },
];

const services = [
  { 
    icon: Calculator, 
    title: "Individual Tax Preparation", 
    description: "W-2, investment, and rental income returns prepared accurately and thoroughly.",
    href: "/services#individual"
  },
  { 
    icon: Briefcase, 
    title: "Self-Employed & 1099 Tax", 
    description: "Schedule C, gig-worker, and contractor returns with quarterly estimate guidance.",
    href: "/services#self-employed"
  },
  { 
    icon: Building2, 
    title: "Small-Business Tax", 
    description: "LLC, S-Corp, and C-Corp returns with year-round planning support.",
    href: "/services#business"
  },
  { 
    icon: TrendingUp, 
    title: "Tax Planning & Estimates", 
    description: "Proactive quarterly planning to help you stay ahead and avoid surprises.",
    href: "/services#planning"
  },
  { 
    icon: BookOpen, 
    title: "Bookkeeping & Accounting", 
    description: "Monthly reconciliations, financial reports, and clean books year-round.",
    href: "/bookkeeping"
  },
  { 
    icon: FileWarning, 
    title: "IRS & State Notice Assistance", 
    description: "Guidance on responding to tax notices and coordinating resolution support.",
    href: "/services#notices"
  },
  { 
    icon: Bot, 
    title: "AI Automation for Small Businesses", 
    description: "Better lead intake, scheduling, reminders, CRM setup, and AI-assisted workflows.",
    href: "/ai-automation"
  },
];

const steps = [
  { 
    icon: ClipboardCheck, 
    number: "01", 
    title: "Start Your Return", 
    description: "Complete a brief, non-sensitive assessment so we understand your needs." 
  },
  { 
    icon: CalendarCheck, 
    number: "02", 
    title: "Book or Begin Securely", 
    description: "Book a consultation or receive clear next-step instructions." 
  },
  { 
    icon: Upload, 
    number: "03", 
    title: "Upload Through the Secure Portal", 
    description: "Accepted clients use MyTAXPrepOffice to securely submit documents and complete signatures." 
  },
  { 
    icon: Send, 
    number: "04", 
    title: "Prepare, Review & File", 
    description: "We prepare and review your return, request any needed info, then coordinate signatures, payment, and e-filing when eligible." 
  },
];

export default function Home() {
  const settings = useSettings();
  const PORTAL_URL = settings.portal_url;
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await axios.get(`${API}/testimonials`);
        setTestimonials(res.data);
      } catch (e) {
        console.log("Using placeholder testimonials");
        setTestimonials([
          { id: "1", name: "Michael R.", role: "Small Business Owner", content: "DA TAXGUY saved me hours of stress. Their portal made uploading documents easy, and my return was done in days.", rating: 5 },
          { id: "2", name: "Sarah K.", role: "Freelance Designer", content: "As a freelancer, taxes used to terrify me. Now I have year-round support and finally understand my quarterly estimates.", rating: 5 },
          { id: "3", name: "James T.", role: "Real Estate Professional", content: "Professional, responsive, and thorough. They helped me with multiple properties and business entities seamlessly.", rating: 5 },
        ]);
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-navy text-white noise-overlay overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 opacity-0 animate-fade-in">
              <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">
                NYC-Based • Nationwide Service
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Tax Preparation & Accounting Services Built for Busy New Yorkers — 
                <span className="text-bronze"> and Clients Nationwide.</span>
              </h1>
              <p className="font-body text-lg md:text-xl text-gray-300 mb-8 max-w-2xl">
                File with confidence, stay compliant, and keep your finances organized with dependable tax preparation, accounting, bookkeeping, and business support.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 hover:-translate-y-1 shadow-lg"
                  data-testid="hero-start-btn"
                >
                  <Link to="/start">
                    Start Your Return
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-navy rounded-full px-8 py-6 text-lg font-medium transition-all duration-300"
                  onClick={() => window.dispatchEvent(new CustomEvent("open-booking"))}
                  data-testid="hero-book-btn"
                >
                  Book a Consultation
                </Button>
              </div>
              <a
                href={PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-6 text-bronze hover:text-bronze/80 transition-colors font-medium"
                data-testid="hero-portal-link"
              >
                Existing Client? Secure Portal Login
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            <div className="lg:col-span-5">
              <div className="relative mt-8 lg:mt-0">
                <div className="absolute -inset-4 bg-bronze/20 rounded-full blur-3xl"></div>
                <img 
                  src="https://images.unsplash.com/photo-1616001944944-9aa3059177e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxueWMlMjBza3lsaW5lJTIwYXJjaGl0ZWN0dXJlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3MDMyMTI0NHww&ixlib=rb-4.1.0&q=85&w=600"
                  alt="NYC Skyline"
                  className="relative rounded-2xl shadow-2xl w-full object-cover max-h-64 lg:max-h-none"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {trustItems.map((item, index) => (
              <div 
                key={item.label} 
                className={`flex items-center gap-3 opacity-0 animate-fade-in stagger-${index + 1}`}
              >
                <div className="w-12 h-12 rounded-full bg-bone flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-bronze" />
                </div>
                <span className="font-body font-medium text-gray-800 text-sm">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Our Services</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-4">
              Tax & Accounting, Done Right.
            </h2>
            <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive financial services for individuals and businesses, 
              delivered with professionalism and care.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link 
                key={service.title} 
                to={service.href}
                className={`opacity-0 animate-fade-in stagger-${index + 1}`}
              >
                <Card 
                  className="service-card h-full hover-lift cursor-pointer"
                  data-testid={`service-card-${index}`}
                >
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-xl bg-bone flex items-center justify-center mb-6">
                      <service.icon className="service-icon w-7 h-7 text-navy transition-colors" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-navy mb-3">
                      {service.title}
                    </h3>
                    <p className="font-body text-gray-600">
                      {service.description}
                    </p>
                    <div className="mt-6 flex items-center text-bronze font-medium">
                      Learn more <ArrowRight className="ml-2 w-4 h-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Tax Preparation Pricing */}
      <section id="pricing" className="bg-white py-20 md:py-28 scroll-mt-24" data-testid="pricing-section" aria-labelledby="pricing-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Transparent Pricing</p>
            <h2 id="pricing-heading" className="font-heading text-3xl md:text-4xl font-bold text-navy mb-3">
              Tax Preparation Pricing
            </h2>
            <p className="font-body text-lg text-gray-700 mb-4">
              Clear starting prices. Secure service. Professional review.
            </p>
            <p className="font-body text-gray-600 max-w-3xl mx-auto">
              Our minimum professional tax preparation fee is <strong className="text-navy">$650</strong>. Final pricing is based on the forms required, income types, states, records provided, and overall complexity of your tax situation.
            </p>
          </div>

          {/* Pricing cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                title: "Standard Individual Tax Return",
                price: "Starts at $650",
                featured: false,
                items: [
                  "Federal and New York individual filing where applicable",
                  "Secure client portal and document handling",
                  "Professional tax return review",
                  "E-file submission",
                  "Reasonable post-filing support",
                ],
                footer: null,
              },
              {
                title: "Self-Employed / Schedule C",
                price: "Starts at $850–$1,000+",
                featured: true,
                items: [
                  "Individual return with business income and expenses",
                  "Review of sole-proprietor records",
                  "Schedule C preparation",
                  "Secure portal document upload",
                  "Professional review before e-file",
                ],
                footer: "Final fee depends on record quality and business complexity.",
              },
              {
                title: "Complex Individual Returns",
                price: "Starts at $1,000+",
                featured: false,
                items: [
                  "Rental property income",
                  "K-1 income",
                  "Multiple-state returns",
                  "Investments and cryptocurrency",
                  "Foreign income or reporting needs",
                ],
                footer: "Pricing is quoted after document review.",
              },
            ].map((plan, i) => (
              <Card
                key={plan.title}
                className={`relative flex flex-col h-full border shadow-lg transition-all duration-300 hover:-translate-y-1 ${plan.featured ? "border-bronze border-2 bg-bone" : "border-gray-100 bg-white"}`}
                data-testid={`pricing-card-${i}`}
              >
                {plan.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-bronze text-navy text-xs font-semibold px-4 py-1 rounded-full">
                    Most Common
                  </span>
                )}
                <CardContent className="p-8 flex flex-col h-full">
                  <h3 className="font-heading text-xl font-semibold text-navy mb-2 min-h-[3.5rem] flex items-center">
                    {plan.title}
                  </h3>
                  <p className="font-heading text-2xl font-bold text-bronze mb-6">{plan.price}</p>
                  <ul className="space-y-3 mb-6 flex-1">
                    {plan.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-3 font-body text-sm text-gray-700">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  {plan.footer && (
                    <p className="font-body text-xs text-gray-500 italic pt-4 border-t border-gray-200">
                      {plan.footer}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Common Add-Ons */}
          <div className="max-w-3xl mx-auto bg-bone rounded-2xl p-6 md:p-8 mb-10" data-testid="pricing-addons">
            <h4 className="font-heading text-lg font-semibold text-navy mb-4">Common Add-Ons</h4>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "Additional state return: $125–$200",
                "Amended return: $300–$600+",
                "Rush service: quoted when available",
                "Additional schedules, forms, and complex reporting: quoted based on complexity",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 font-body text-sm text-gray-700">
                  <span className="text-bronze mt-1">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Refund Transfer Callout */}
          <div className="max-w-4xl mx-auto bg-navy text-white noise-overlay rounded-2xl p-8 md:p-10 mb-8" data-testid="pricing-callout">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 text-bronze" />
              </div>
              <div>
                <h3 className="font-heading text-2xl font-bold mb-3">Pay From Your Refund Option Available</h3>
                <p className="font-body text-gray-300 mb-4">
                  Eligible clients may choose a refund transfer option that allows authorized tax-preparation fees and certain bank-related charges to be deducted from their tax refund instead of being paid upfront.
                </p>
                <p className="font-body text-xs text-gray-400 mb-6">
                  Refund transfer availability is subject to bank approval, eligibility, applicable terms, and fees. This is not a loan. It does not guarantee a refund amount, refund timing, or faster IRS processing.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    asChild
                    className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-base font-semibold"
                    data-testid="pricing-start-btn"
                  >
                    <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer">
                      Start Your Tax Prep
                      <ExternalLink className="ml-2 w-4 h-4" />
                    </a>
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.dispatchEvent(new CustomEvent("open-booking"))}
                    className="border-white text-white hover:bg-white hover:text-navy rounded-full px-8 py-6 text-base font-medium bg-transparent"
                    data-testid="pricing-book-btn"
                  >
                    Book a Consultation
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center font-body text-sm text-gray-500 max-w-3xl mx-auto">
            Prices are starting estimates and may change if additional forms, states, corrections, missing documentation, or substantial follow-up work are required.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Simple Process</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-4">
              How It Works
            </h2>
            <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto">
              Getting started is easy. Our streamlined process makes tax preparation simple and stress-free.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div 
                key={step.number} 
                className={`relative opacity-0 animate-fade-in stagger-${index + 1}`}
                data-testid={`step-${index + 1}`}
              >
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gray-200 -translate-x-1/2"></div>
                )}
                <div className="text-center">
                  <div className="relative inline-block mb-6">
                    <div className="w-24 h-24 rounded-full bg-bone flex items-center justify-center mx-auto">
                      <step.icon className="w-10 h-10 text-bronze" />
                    </div>
                    <span className="absolute -top-2 -right-2 font-mono text-4xl font-bold text-bronze/30">
                      {step.number}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-navy mb-3">
                    {step.title}
                  </h3>
                  <p className="font-body text-gray-600">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center font-body text-sm text-gray-500 mt-10 max-w-2xl mx-auto">
            Tax results vary based on each client's unique facts, records, and circumstances.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Testimonials</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-4">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <Card 
                key={testimonial.id} 
                className={`bg-white border-0 shadow-lg opacity-0 animate-fade-in stagger-${index + 1}`}
                data-testid={`testimonial-${index}`}
              >
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-bronze text-bronze" />
                    ))}
                  </div>
                  <p className="font-body text-gray-700 mb-6 italic">
                    "{testimonial.content}"
                  </p>
                  <div className="gold-border-left pl-4">
                    <p className="font-body font-semibold text-navy">{testimonial.name}</p>
                    <p className="font-body text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {settings.google_review_url && (
            <div className="text-center mt-12">
              <Button
                asChild
                variant="outline"
                className="border-navy text-navy hover:bg-navy hover:text-white rounded-full px-8 py-5 font-medium"
                data-testid="google-review-btn"
              >
                <a href={settings.google_review_url} target="_blank" rel="noopener noreferrer">
                  <Star className="mr-2 w-5 h-5 fill-bronze text-bronze" />
                  Leave a Google Review
                </a>
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Band */}
      <section className="bg-navy text-white noise-overlay py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="font-body text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
            Join hundreds of satisfied clients who trust DA TAXGUY & ASSOCIATES with their tax and accounting needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 hover:-translate-y-1"
              data-testid="cta-start-btn"
            >
              <Link to="/start">
                Start Your Return
              </Link>
            </Button>
            <Button
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-navy rounded-full px-8 py-6 text-lg font-medium transition-all duration-300"
              onClick={() => window.dispatchEvent(new CustomEvent("open-booking"))}
              data-testid="cta-book-btn"
            >
              Book a Consultation
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-bronze text-bronze hover:bg-bronze hover:text-navy rounded-full px-8 py-6 text-lg font-medium transition-all duration-300"
              data-testid="cta-portal-btn"
            >
              <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer">
                Client Portal Login
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
